
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { MessageSquare, Send, User, Phone, Mail } from 'lucide-react';

interface RentalRequestFormProps {
  propertyId: string;
  propertyTitle: string;
  onSuccess?: () => void;
}

const RentalRequestForm = ({ propertyId, propertyTitle, onSuccess }: RentalRequestFormProps) => {
  const [message, setMessage] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [tenantPhone, setTenantPhone] = useState('');
  const [tenantEmail, setTenantEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!tenantName.trim() || !tenantPhone.trim() || !tenantEmail.trim()) {
      toast({
        title: "معلومات ناقصة",
        description: "يرجى ملء جميع الحقول المطلوبة",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          title: "خطأ في المصادقة",
          description: "يجب تسجيل الدخول لإرسال طلب الإيجار",
          variant: "destructive",
        });
        return;
      }

      console.log('إرسال طلب إيجار للعقار:', propertyId);
      console.log('بيانات المستخدم:', user.id);

      // إرسال طلب الإيجار مع البيانات المطلوبة فقط
      const requestData = {
        user_id: user.id,
        property_id: propertyId,
        tenant_name: tenantName.trim(),
        tenant_phone: tenantPhone.trim(),
        tenant_email: tenantEmail.trim(),
        message: message.trim() || null,
        status: 'pending'
      };

      const { data, error } = await supabase
        .from('rental_requests')
        .insert([requestData])
        .select();

      if (error) {
        let errorMessage = "حدث خطأ أثناء إرسال طلب الإيجار";
        
        if (error.code === '23505') {
          errorMessage = "لقد تم إرسال طلب مسبقاً لهذا العقار";
        } else if (error.code === '42501') {
          errorMessage = "ليس لديك صلاحية لإرسال هذا الطلب";
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        toast({
          title: "خطأ في الإرسال",
          description: errorMessage,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "تم الإرسال بنجاح",
        description: "تم إرسال طلب الإيجار للمالك وسيتم الرد عليك قريباً",
      });

      // إعادة تعيين النموذج
      setMessage('');
      setTenantName('');
      setTenantPhone('');
      setTenantEmail('');

      if (onSuccess) {
        onSuccess();
      }

    } catch (error) {
      toast({
        title: "خطأ في الإرسال",
        description: "حدث خطأ غير متوقع. يرجى المحاولة مرة أخرى",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="text-right flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-blue-600" />
          طلب إيجار العقار
        </CardTitle>
        <p className="text-sm text-gray-600 text-right">
          املأ النموذج أدناه لإرسال طلب إيجار للعقار: {propertyTitle}
        </p>
      </CardHeader>
      
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* معلومات المتقدم */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="tenant-name" className="text-right flex items-center gap-2">
                <User className="h-4 w-4" />
                الاسم الكامل *
              </Label>
              <Input
                id="tenant-name"
                type="text"
                value={tenantName}
                onChange={(e) => setTenantName(e.target.value)}
                placeholder="أدخل اسمك الكامل"
                className="text-right"
                required
                disabled={isSubmitting}
                autoComplete="name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="tenant-phone" className="text-right flex items-center gap-2">
                <Phone className="h-4 w-4" />
                رقم الهاتف *
              </Label>
              <Input
                id="tenant-phone"
                type="tel"
                value={tenantPhone}
                onChange={(e) => setTenantPhone(e.target.value)}
                placeholder="أدخل رقم هاتفك"
                className="text-right"
                required
                disabled={isSubmitting}
                autoComplete="tel"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tenant-email" className="text-right flex items-center gap-2">
              <Mail className="h-4 w-4" />
              البريد الإلكتروني *
            </Label>
            <Input
              id="tenant-email"
              type="email"
              value={tenantEmail}
              onChange={(e) => setTenantEmail(e.target.value)}
              placeholder="أدخل بريدك الإلكتروني"
              className="text-right"
              required
              disabled={isSubmitting}
              autoComplete="email"
            />
          </div>

          {/* رسالة إضافية */}
          <div className="space-y-2">
            <Label htmlFor="message" className="text-right">
              رسالة إضافية (اختيارية)
            </Label>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="اكتب رسالة للمالك حول اهتمامك بالعقار أو أي استفسارات..."
              className="text-right min-h-[100px]"
              rows={4}
              disabled={isSubmitting}
            />
          </div>

          {/* زر الإرسال */}
          <Button 
            type="submit" 
            className="w-full" 
            disabled={isSubmitting}
          >
            <Send className="h-4 w-4 mr-2" />
            {isSubmitting ? 'جاري الإرسال...' : 'إرسال طلب الإيجار'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default RentalRequestForm;
